import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";

interface ContactForm {
  firstName: string; lastName: string; salutation: string; title: string; degree: string;
  email: string; phone: string; mobile: string; linkedinUrl: string;
  accountId: string; leadSource: string;
  tags: string; solutions: string; cbcOfferings: string; contactGeography: string;
  influenceRole: string; communicationPref: string;
  description: string;
  mailingStreet: string; mailingCity: string; mailingState: string; mailingZip: string; mailingCountry: string;
  otherStreet: string;   otherCity: string;   otherState: string;   otherZip: string;   otherCountry: string;
  optedOut: boolean; unsubscribedMode: string;
  locked: boolean;
  fax: string; homePhone: string; otherPhone: string;
  dateOfBirth: string; department: string;
  assistantName: string; assistantPhone: string;
  reportsToId: string;
}

const EMPTY: ContactForm = {
  firstName: "", lastName: "", salutation: "", title: "", degree: "",
  email: "", phone: "", mobile: "", linkedinUrl: "",
  accountId: "", leadSource: "",
  tags: "", solutions: "", cbcOfferings: "", contactGeography: "",
  influenceRole: "UNKNOWN", communicationPref: "EMAIL",
  description: "",
  mailingStreet: "", mailingCity: "", mailingState: "", mailingZip: "", mailingCountry: "",
  otherStreet: "",   otherCity: "",   otherState: "",   otherZip: "",   otherCountry: "",
  optedOut: false, unsubscribedMode: "",
  locked: false,
  fax: "", homePhone: "", otherPhone: "",
  dateOfBirth: "", department: "",
  assistantName: "", assistantPhone: "",
  reportsToId: "",
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="grid gap-4 pt-0 md:grid-cols-2">{children}</CardContent>
    </Card>
  );
}

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

export default function ContactNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<ContactForm>(EMPTY);

  useEffect(() => {
    api.get("/accounts", { params: { pageSize: 100, sortBy: "name", sortDir: "asc" } })
      .then((r) => setAccounts(r.data.data ?? []))
      .catch(() => setAccounts([]));
  }, []);

  function set<K extends keyof ContactForm>(k: K, v: ContactForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        if (k === "tags") payload.tags = (v as string).split(",").map((s) => s.trim()).filter(Boolean);
        else payload[k] = v;
      }
      await api.post("/contacts", payload);
      qc.invalidateQueries({ queryKey: ["/contacts"] });
      navigate("/contacts");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create contact.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="New Contact" description="Full Zoho-style contact record." />
      <form onSubmit={submit} className="p-6">
        <div className="mx-auto max-w-4xl space-y-4">
          <Section title="Identity">
            <Field id="salutation" label="Salutation">
              <Select id="salutation" value={form.salutation} onChange={(e) => set("salutation", e.target.value)}>
                <option value="">—</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Dr.">Dr.</option>
                <option value="Prof.">Prof.</option>
              </Select>
            </Field>
            <Field id="title" label="Title">
              <Input id="title" value={form.title} onChange={(e) => set("title", e.target.value)} />
            </Field>
            <Field id="firstName" label="First name *">
              <Input id="firstName" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
            </Field>
            <Field id="lastName" label="Last name *">
              <Input id="lastName" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
            </Field>
            <Field id="degree" label="Degree">
              <Input id="degree" value={form.degree} onChange={(e) => set("degree", e.target.value)} />
            </Field>
            <Field id="accountId" label="Account">
              <Select id="accountId" value={form.accountId} onChange={(e) => set("accountId", e.target.value)}>
                <option value="">— None —</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </Field>
          </Section>

          <Section title="Contact channels">
            <Field id="email" label="Email">
              <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </Field>
            <Field id="phone" label="Phone">
              <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
            <Field id="mobile" label="Mobile">
              <Input id="mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} />
            </Field>
            <Field id="linkedinUrl" label="LinkedIn">
              <Input id="linkedinUrl" type="url" placeholder="https://linkedin.com/in/…" value={form.linkedinUrl} onChange={(e) => set("linkedinUrl", e.target.value)} />
            </Field>
            <Field id="communicationPref" label="Preferred contact">
              <Select id="communicationPref" value={form.communicationPref} onChange={(e) => set("communicationPref", e.target.value)}>
                <option value="EMAIL">Email</option>
                <option value="PHONE">Phone</option>
                <option value="LINKEDIN">LinkedIn</option>
                <option value="SMS">SMS</option>
                <option value="NO_CONTACT">No contact</option>
              </Select>
            </Field>
            <Field id="influenceRole" label="Influence">
              <Select id="influenceRole" value={form.influenceRole} onChange={(e) => set("influenceRole", e.target.value)}>
                <option value="UNKNOWN">Unknown</option>
                <option value="DECISION_MAKER">Decision maker</option>
                <option value="CHAMPION">Champion</option>
                <option value="INFLUENCER">Influencer</option>
                <option value="GATEKEEPER">Gatekeeper</option>
                <option value="END_USER">End user</option>
                <option value="BLOCKER">Blocker</option>
              </Select>
            </Field>
          </Section>

          <Section title="Attribution & categorization">
            <Field id="leadSource" label="Lead source">
              <Input id="leadSource" placeholder="Apollo, LinkedIn, Webinar, …" value={form.leadSource} onChange={(e) => set("leadSource", e.target.value)} />
            </Field>
            <Field id="contactGeography" label="Contact geography">
              <Input id="contactGeography" placeholder="APAC, EMEA, NA-East, …" value={form.contactGeography} onChange={(e) => set("contactGeography", e.target.value)} />
            </Field>
            <Field id="solutions" label="Solutions of interest">
              <Input id="solutions" placeholder="Data & AI, Cloud, Staffing, …" value={form.solutions} onChange={(e) => set("solutions", e.target.value)} />
            </Field>
            <Field id="cbcOfferings" label="CBC offerings">
              <Input id="cbcOfferings" value={form.cbcOfferings} onChange={(e) => set("cbcOfferings", e.target.value)} />
            </Field>
            <div className="md:col-span-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input id="tags" placeholder="VIP, Net New, Summit 2026" value={form.tags} onChange={(e) => set("tags", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
          </Section>

          <Section title="Mailing address">
            <div className="md:col-span-2">
              <Label htmlFor="mailingStreet">Street</Label>
              <Input id="mailingStreet" value={form.mailingStreet} onChange={(e) => set("mailingStreet", e.target.value)} />
            </div>
            <Field id="mailingCity" label="City">
              <Input id="mailingCity" value={form.mailingCity} onChange={(e) => set("mailingCity", e.target.value)} />
            </Field>
            <Field id="mailingState" label="State / region">
              <Input id="mailingState" value={form.mailingState} onChange={(e) => set("mailingState", e.target.value)} />
            </Field>
            <Field id="mailingZip" label="ZIP / postcode">
              <Input id="mailingZip" value={form.mailingZip} onChange={(e) => set("mailingZip", e.target.value)} />
            </Field>
            <Field id="mailingCountry" label="Country">
              <Input id="mailingCountry" value={form.mailingCountry} onChange={(e) => set("mailingCountry", e.target.value)} />
            </Field>
          </Section>

          <Section title="Other address (optional)">
            <div className="md:col-span-2">
              <Label htmlFor="otherStreet">Street</Label>
              <Input id="otherStreet" value={form.otherStreet} onChange={(e) => set("otherStreet", e.target.value)} />
            </div>
            <Field id="otherCity" label="City">
              <Input id="otherCity" value={form.otherCity} onChange={(e) => set("otherCity", e.target.value)} />
            </Field>
            <Field id="otherState" label="State / region">
              <Input id="otherState" value={form.otherState} onChange={(e) => set("otherState", e.target.value)} />
            </Field>
            <Field id="otherZip" label="ZIP / postcode">
              <Input id="otherZip" value={form.otherZip} onChange={(e) => set("otherZip", e.target.value)} />
            </Field>
            <Field id="otherCountry" label="Country">
              <Input id="otherCountry" value={form.otherCountry} onChange={(e) => set("otherCountry", e.target.value)} />
            </Field>
          </Section>

          <Section title="Additional information">
            <Field id="fax" label="Fax">
              <Input id="fax" value={form.fax} onChange={(e) => set("fax", e.target.value)} />
            </Field>
            <Field id="homePhone" label="Home phone">
              <Input id="homePhone" value={form.homePhone} onChange={(e) => set("homePhone", e.target.value)} />
            </Field>
            <Field id="otherPhone" label="Other phone">
              <Input id="otherPhone" value={form.otherPhone} onChange={(e) => set("otherPhone", e.target.value)} />
            </Field>
            <Field id="dateOfBirth" label="Date of birth">
              <Input id="dateOfBirth" type="date" value={form.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} />
            </Field>
            <Field id="department" label="Department">
              <Input id="department" placeholder="IT, Sales, HR…" value={form.department} onChange={(e) => set("department", e.target.value)} />
            </Field>
            <Field id="assistantName" label="Assistant name">
              <Input id="assistantName" value={form.assistantName} onChange={(e) => set("assistantName", e.target.value)} />
            </Field>
            <Field id="assistantPhone" label="Assistant phone">
              <Input id="assistantPhone" value={form.assistantPhone} onChange={(e) => set("assistantPhone", e.target.value)} />
            </Field>
          </Section>

          <Section title="Compliance">
            <div className="flex items-center gap-2">
              <input id="optedOut" type="checkbox" checked={form.optedOut} onChange={(e) => set("optedOut", e.target.checked)} />
              <Label htmlFor="optedOut">Opted out / unsubscribed</Label>
            </div>
            <Field id="unsubscribedMode" label="Unsubscribed mode">
              <Select id="unsubscribedMode" value={form.unsubscribedMode} onChange={(e) => set("unsubscribedMode", e.target.value)}>
                <option value="">—</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="all">All channels</option>
              </Select>
            </Field>
            <div className="flex items-center gap-2">
              <input id="locked" type="checkbox" checked={form.locked} onChange={(e) => set("locked", e.target.checked)} />
              <Label htmlFor="locked">Lock record (read-only)</Label>
            </div>
          </Section>

          <div className="flex items-center justify-between rounded-md border bg-card px-6 py-4">
            {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => navigate("/contacts")}>Cancel</Button>
              <Button type="submit" disabled={busy || (!form.firstName && !form.lastName)}>
                {busy ? "Saving…" : "Create"}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
